import { useState, useEffect } from 'react';
import { IconButton, Avatar, Chip, Skeleton } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import DOMPurify from 'dompurify';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import EnterContactNumberForm from '@LandingForms/EnterContactNumberForm';
import VerifyUpdateEmailForm from '@LandingForms/VerifyUpdateEmailForm';
import ErrorHandler from '@Root/ErrorHandler';
import { getCountries, getStates } from 'country-state-picker';
import {
    IoPersonOutline,
    IoMailOutline,
    IoLocationOutline,
    IoBriefcaseOutline,
    IoInformationCircleOutline,
    IoCamera,
    IoTrash,
    IoCheckmarkCircle,
    IoPencil,
    IoBusinessOutline,
} from 'react-icons/io5';
import { useReduxUsers } from '@Hooks/reduxUsersHook/useReduxUsers';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './ProfilePage.scss';

export default function ProfilePage(props) {
    const [cursor, setCursor] = useState('auto');
    const [user, setUser] = useState();
    const [, setEmail] = useState('');
    const [, setPhoto] = useState('');
    const [fullName, setFullName] = useState('');
    const [designation, setDesignation] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [, setUploadStatus] = useState('');
    const [edited, setEdited] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [, setIsValidImage] = useState(true);
    const [states, setStates] = useState([]);
    const [country, setCountry] = useState('');
    const [userState, setUserState] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [userCountry, setUserCountry] = useState({});
    const [showEnterContactNumberForm, setShowEnterContactNumberForm] = useState(false);
    const [openVerifyEmailForm, setOpenVerifyEmailForm] = useState(false);
    const [loading, setLoading] = useState(true);

    const { setUpdateUser } = useReduxUsers();
    const { setActiveUser, activeUser } = useReduxActiveUser();
    const userInfoService = new UserInfoService();
    new OrgService();
    const errorHandler = new ErrorHandler();
    let countries = getCountries();
    const APC_ENABLE_INTEGRATIONS_WHATSAPP = JSON.parse(localStorage.getItem('APC_ENABLE_INTEGRATIONS_WHATSAPP'));
    const cn = 'profilePage';

    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060'];

    useEffect(() => { renderData(); }, []);

    useEffect(() => {
        if (countryCode) setStates(getStates(countryCode));
    }, [countryCode]);

    const renderData = async () => {
        try {
            setLoading(true);
            let userBody = activeUser;
            setCountry(userBody.country ?? '');
            setCountryCode(userBody.countryCode ?? '');
            setUserState(userBody.state ?? '');
            setUserCountry({ name: userBody.country ?? '', code: userBody.countryCode ?? '' });
            setUser(userBody);
            setFullName(userBody.fullName);
            setEmail(userBody.email);
            setDesignation(userBody.designation);
            setImageUrl(!!userBody.photo ? URL.createObjectURL(dataURLtoFile(userBody.photo)) : '');
            setEdited(false);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleEnterContactNumberForm = () => setShowEnterContactNumberForm(p => !p);
    const toggleVerifyEmailUpdateForm  = () => setOpenVerifyEmailForm(p => !p);
    const timeout = (delay) => new Promise(res => setTimeout(res, delay));

    const handleImageChange = () => document.getElementById('fileInput').click();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (validateImage(file)) {
            const base64File = await convertFileToBase64(file);
            setPhoto(base64File);
            setImageUrl(URL.createObjectURL(file));
            setIsValidImage(true);
            setCursor('wait');
            await timeout(300);
            let userObj = await userInfoService.updateProfilePicture(activeUser.id, base64File);
            setUpdateUser(activeUser.id, { body: userObj.body });
            userObj = JSON.parse(userObj.body);
            setUser(userObj);
            setActiveUser(userObj);
            SnackbarManager.success('Profile image updated');
            setCursor('auto');
        } else {
            setIsValidImage(false);
        }
    };

    const handleFullNameChange    = (e) => { setFullName(DOMPurify.sanitize(e.target.value)); setEdited(true); };
    const handleDesignationChange = (e) => { setDesignation(DOMPurify.sanitize(e.target.value)); setEdited(true); };
    const handleAboutChange       = (e) => { setUser(p => ({ ...(p || {}), about: DOMPurify.sanitize(e.target.value) })); setEdited(true); };
    const handleCompanyChange     = (e) => { setUser(p => ({ ...(p || {}), company: DOMPurify.sanitize(e.target.value) })); setEdited(true); };

    const handleCountryChange = (e, value) => {
        setEdited(true);
        if (!value) { setCountry(''); setCountryCode(''); setUserCountry(null); setStates([]); setUserState(''); return; }
        setStates(getStates(value.code));
        setCountry(value.name);
        setCountryCode(value.code);
        setUserCountry(value);
        setUserState('');
    };

    const handleStateChange = (e, value) => { setEdited(true); setUserState(value || ''); };

    const handleSubmit = async () => {
        setCursor('wait');
        try {
            if (validateEmail(user.email)) {
                let userObj = { ...user, country, countryCode, state: userState, fullName, designation };
                setEdited(false);
                let updatedUser = await userInfoService.updateUserInfo(userObj);
                if (updatedUser.body == undefined) { SnackbarManager.error('Failed to update. Please try again!'); return; }
                setUpdateUser(activeUser.id, { body: updatedUser.body });
                updatedUser = JSON.parse(updatedUser.body);
                setUser(updatedUser);
                setActiveUser(updatedUser);
                SnackbarManager.success('Successfully updated profile');
            } else {
                setIsValidEmail(false);
                SnackbarManager.error('Please enter a valid email');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const dataURLtoFile = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        const ext = mime.split('/')[1] || 'jpg';
        return new File([u8arr], `photo.${ext}`, { type: mime });
    };

    const validateEmail = (value) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(value || '');

    const validateImage = (file) => {
        if (file.type.split('/')[0] === 'image') {
            if (file.size > 1024 * 1024) {
                setUploadStatus('Image size exceeded 1MB!');
                SnackbarManager.error('Image size exceeded 1MB!');
                return false;
            }
            return true;
        }
        setUploadStatus('Upload image file!');
        return false;
    };

    const handleRemoveProfilePicture = async () => {
        setCursor('wait');
        try {
            if (!user?.photo) return;
            let response = await userInfoService.removeProfilePicture(user.id);
            if (response.status < 400) {
                setUpdateUser(activeUser.id, { body: response.body });
                let updatedUser = JSON.parse(response.body);
                setUser(updatedUser);
                setActiveUser(updatedUser);
                setImageUrl('');
                SnackbarManager.success(response.message || 'Profile image removed');
            } else {
                SnackbarManager.error(response.message || 'Unable to remove image');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : name.substring(0, 2).toUpperCase();
    };

    const fieldSx = { borderRadius: '10px', backgroundColor: 'var(--color-muiBackground)' };

    if (loading) {
        return (
            <div className={cn}>
                <div className={`${cn}__container`}>
                    <div className={`${cn}__card`}>
                        <Skeleton variant="circular" width={80} height={80} />
                        <div style={{ flex: 1 }}>
                            <Skeleton width="35%" height={28} />
                            <Skeleton width="22%" height={20} sx={{ mt: 0.5 }} />
                            <Skeleton width="45%" height={18} sx={{ mt: 0.75 }} />
                        </div>
                    </div>
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className={`${cn}__section`}>
                            <Skeleton width="28%" height={22} sx={{ mb: 1.5 }} />
                            <Skeleton width="100%" height={50} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className={cn} style={{ cursor }}>
            <div className={`${cn}__container`}>

                {/* ── Avatar / identity card ──────────────────────────── */}
                <div className={`${cn}__card`}>
                    <input type="file" accept="image/jpeg,image/png,image/webp" id="fileInput" onChange={handleFileChange} style={{ display: 'none' }} />

                    <div className={`${cn}__avatar`}>
                        {imageUrl ? (
                            <img src={imageUrl} alt="profile" className={`${cn}__avatar__img`} />
                        ) : (
                            <Avatar
                                sx={{
                                    width: 80, height: 80,
                                    fontSize: '1.6rem', fontWeight: 700,
                                    bgcolor: colorList[fullName?.charCodeAt(0) % colorList.length]
                                }}
                            >
                                {getInitials(fullName)}
                            </Avatar>
                        )}
                        <div className={`${cn}__avatar__overlay`}>
                            <IconButton size="small" className={`${cn}__avatar__btn`} onClick={handleImageChange}>
                                <IoCamera />
                            </IconButton>
                            {!!imageUrl && (
                                <IconButton size="small" className={`${cn}__avatar__btn ${cn}__avatar__btn--delete`} onClick={handleRemoveProfilePicture}>
                                    <IoTrash />
                                </IconButton>
                            )}
                        </div>
                    </div>

                    <div className={`${cn}__identity`}>
                        <div className={`${cn}__identity__row`}>
                            <h2 className={`${cn}__identity__name`}>{fullName || 'User'}</h2>
                            <Chip
                                label={`@${user?.userName}`}
                                size="small"
                                variant="outlined"
                                className={`${cn}__identity__chip`}
                            />
                        </div>
                        <div className={`${cn}__identity__meta`}>
                            {designation && (
                                <span><IoBriefcaseOutline /> {designation}</span>
                            )}
                            {user?.company && (
                                <span><IoBusinessOutline /> {user?.company}</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className={`${cn}__divider`} />

                {/* ── Personal Information ────────────────────────────── */}
                <div className={`${cn}__section`}>
                    <div className={`${cn}__section__head`}>
                        <IoPersonOutline />
                        <h3>Personal Information</h3>
                    </div>
                    <div className={`${cn}__grid`}>
                        <TextField
                            label="Full Name"
                            value={fullName || ''}
                            onChange={handleFullNameChange}
                            fullWidth size="small" variant="outlined"
                            InputProps={{ sx: fieldSx }}
                        />
                        <TextField
                            label="Designation"
                            value={designation || ''}
                            onChange={handleDesignationChange}
                            fullWidth size="small" variant="outlined"
                            InputProps={{ sx: fieldSx }}
                        />
                        <TextField
                            label="Company"
                            value={user?.company || ''}
                            onChange={handleCompanyChange}
                            fullWidth size="small" variant="outlined"
                            InputProps={{ sx: fieldSx }}
                        />
                    </div>
                </div>

                {/* ── Contact Information ─────────────────────────────── */}
                <div className={`${cn}__section`}>
                    <div className={`${cn}__section__head`}>
                        <IoMailOutline />
                        <h3>Contact Information</h3>
                    </div>
                    <div className={`${cn}__grid`}>
                        <div className={`${cn}__field-row`}>
                            <TextField
                                label="Email Address"
                                value={user?.email || ''}
                                fullWidth disabled size="small" variant="outlined"
                                error={!isValidEmail}
                                InputProps={{ sx: fieldSx }}
                            />
                            <button type="button" className={`${cn}__action-btn`} onClick={toggleVerifyEmailUpdateForm}>
                                <IoPencil /> Update
                            </button>
                        </div>

                        {APC_ENABLE_INTEGRATIONS_WHATSAPP && (
                            <div className={`${cn}__field-row`}>
                                <TextField
                                    label="Contact Number"
                                    value={user?.contactNumber || 'Not provided'}
                                    fullWidth disabled size="small" variant="outlined"
                                    InputProps={{ sx: fieldSx }}
                                />
                                <button type="button" className={`${cn}__action-btn`} onClick={toggleEnterContactNumberForm}>
                                    <IoPencil /> {user?.contactNumber ? 'Update' : 'Add'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Location ────────────────────────────────────────── */}
                <div className={`${cn}__section`}>
                    <div className={`${cn}__section__head`}>
                        <IoLocationOutline />
                        <h3>Location</h3>
                    </div>
                    <div className={`${cn}__grid`}>
                        <Autocomplete
                            options={countries}
                            value={userCountry || null}
                            getOptionLabel={(option) => option?.name || ''}
                            onChange={handleCountryChange}
                            renderInput={(params) => (
                                <TextField {...params} label="Country" variant="outlined" size="small"
                                    InputProps={{ ...params.InputProps, sx: fieldSx }}
                                />
                            )}
                        />
                        <Autocomplete
                            options={states || []}
                            value={userState || null}
                            getOptionLabel={(option) => option || ''}
                            onChange={handleStateChange}
                            disabled={!countryCode}
                            renderInput={(params) => (
                                <TextField {...params} label="State / Province" variant="outlined" size="small"
                                    InputProps={{ ...params.InputProps, sx: fieldSx }}
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── About ───────────────────────────────────────────── */}
                <div className={`${cn}__section`}>
                    <div className={`${cn}__section__head`}>
                        <IoInformationCircleOutline />
                        <h3>About</h3>
                    </div>
                    <TextField
                        label="Tell us about yourself"
                        value={user?.about || ''}
                        onChange={handleAboutChange}
                        fullWidth multiline rows={4} variant="outlined"
                        placeholder="Write a short bio…"
                        InputProps={{ sx: { ...fieldSx, color: 'var(--color-text)' } }}
                    />
                </div>
            </div>

            {/* ── Save bar ────────────────────────────────────────────── */}
            {edited && (
                <div className={`${cn}__save-bar`}>
                    <span className={`${cn}__save-bar__label`}>You have unsaved changes</span>
                    <div className={`${cn}__save-bar__actions`}>
                        <button type="button" className={`${cn}__save-bar__discard`} onClick={renderData} disabled={cursor === 'wait'}>
                            Discard
                        </button>
                        <button type="button" className={`${cn}__save-bar__save`} onClick={handleSubmit} disabled={cursor === 'wait'}>
                            {cursor === 'wait' ? (
                                <><div className={`${cn}__spinner`} />Saving…</>
                            ) : (
                                <><IoCheckmarkCircle />Save Changes</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {showEnterContactNumberForm && (
                <EnterContactNumberForm
                    open={showEnterContactNumberForm}
                    closePopup={toggleEnterContactNumberForm}
                    reloadData={renderData}
                />
            )}
            {openVerifyEmailForm && (
                <VerifyUpdateEmailForm
                    open={openVerifyEmailForm}
                    closePopup={toggleVerifyEmailUpdateForm}
                    user={user}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}
